"use client";

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { ContactRequestModal } from "@/components/ContactRequestModal";
import { contactModalConfigs, type ContactModalId } from "@/lib/contact-modals";

type ContactModalContextValue = {
  open: (modalId: ContactModalId) => void;
  close: () => void;
};

const ContactModalContext = createContext<ContactModalContextValue | null>(null);

/** Opens the pentest or economic-exploit request modal from anywhere in the tree. */
export function useContactModal() {
  const context = useContext(ContactModalContext);
  if (!context) throw new Error("useContactModal must be used inside <ContactModalProvider>");
  return context;
}

export function ContactModalProvider({ children }: { children: ReactNode }) {
  const [openModal, setOpenModal] = useState<ContactModalId | null>(null);
  const close = useCallback(() => setOpenModal(null), []);
  const open = useCallback((modalId: ContactModalId) => setOpenModal(modalId), []);
  const value = useMemo(() => ({ open, close }), [open, close]);

  return (
    <ContactModalContext.Provider value={value}>
      {children}
      {(Object.keys(contactModalConfigs) as ContactModalId[]).map((modalId) => (
        <ContactRequestModal
          key={modalId}
          config={contactModalConfigs[modalId]}
          isOpen={openModal === modalId}
          onClose={close}
        />
      ))}
    </ContactModalContext.Provider>
  );
}
